import { Button, Dropdown, Icon } from '@billy/design-system'
import { MouseEvent, ReactElement, ReactNode, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ReviewEntriesFilterType } from '../../../enums/reviewEntriesFilterType'
import { ChipLabel, ChipTrigger, FooterRow, PopoverBody, PopoverCard } from '../styles'

export interface ReviewEntriesFilterChipShellProps {
  type: ReviewEntriesFilterType
  label: string
  valuePreview?: string
  isActive: boolean
  shouldAutoOpen?: boolean
  onAutoOpenHandled?: () => void
  onDelete: () => void
  onReset: () => void
  children: ReactNode
}

export const ReviewEntriesFilterChipShell = ({
  type,
  label,
  valuePreview,
  isActive,
  shouldAutoOpen = false,
  onAutoOpenHandled,
  onDelete,
  onReset,
  children,
}: ReviewEntriesFilterChipShellProps): ReactElement => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const chipText = valuePreview ? `${label} : ${valuePreview}` : label

  useEffect(() => {
    if (shouldAutoOpen) {
      setIsOpen(true)
      onAutoOpenHandled?.()
    }
  }, [onAutoOpenHandled, shouldAutoOpen])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleOpen = useCallback(() => {
    setIsOpen(true)
  }, [])

  const handleTriggerMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setIsOpen((previous) => !previous)
  }, [])

  return (
    <Dropdown
      autoToggle={false}
      isOpen={isOpen}
      onClose={handleClose}
      onOpen={handleOpen}
      placement="bottom-start"
      size="xxxl"
      trigger={
        <ChipTrigger data-filter-type={type} isActive={isActive} onMouseDown={handleTriggerMouseDown} type="button">
          <ChipLabel>{chipText}</ChipLabel>
          <Icon color="currentColor" icon="chevronDown" size="sm" />
        </ChipTrigger>
      }
    >
      <PopoverCard>
        <PopoverBody>{children}</PopoverBody>
        <FooterRow data-testid="review-entries-filter-chip-footer">
          <Button color="danger" fullWidth icon="trash" onClick={onDelete} variant="text">
            {t('review_entries.filters.action.delete')}
          </Button>
          <Button fullWidth icon="refresh" onClick={onReset} variant="outlined">
            {t('review_entries.filters.action.reset')}
          </Button>
        </FooterRow>
      </PopoverCard>
    </Dropdown>
  )
}
